import config from '../../config';
import { Api, type ApiConfig } from '../api';
import {
    buildMcpCredentialRequest,
    type CreateMcpConnectorRequest,
    type McpCredentialRequest,
    type McpConnectorPlatform,
    type NormalizedMcpConnectorInput,
} from './mcpConnectorInput';

export type McpConnectorStatus = 'DRAFT' | 'TESTED' | 'ACTIVE' | 'ERROR' | string;

export interface McpConnectorTool {
    id?: number | string | null;
    toolId?: number | string | null;
    name: string;
    mcpToolName?: string | null;
    displayName?: string | null;
    description?: string | null;
    inputSchema?: unknown;
    enabled?: boolean | null;
    accessLevelRequired?: string | null;
    riskLevel?: string | null;
    requireConfirmation?: boolean | null;
    connectorId?: number | string | null;
    integrationId?: number | string | null;
}

export interface McpConnectorToolUpdateRequest {
    enabled?: boolean;
    accessLevelRequired?: 'READ' | 'WRITE';
    riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
    requireConfirmation?: boolean;
}

export interface McpResourceContextUpdateRequest {
    resourceContext: Record<string, string>;
}

export interface McpConnectorResponse {
    connectorId: number;
    integrationId?: number | null;
    displayName: string;
    serverUrl: string;
    transportType: string;
    authType: string;
    status: McpConnectorStatus;
    reasonCode?: string | null;
    platform?: McpConnectorPlatform | null;
    connectionMode?: 'MCP_SERVER' | string | null;
    credentialConfigured?: boolean | null;
    credentialStatus?: string | null;
    canManage?: boolean | null;
    credentialLast4?: string | null;
    protocolVersion?: string | null;
    serverInfo?: unknown;
    resourceContext?: Record<string, string> | null;
    tools?: McpConnectorTool[];
}

export interface McpOAuthStartResponse {
    connectorId: number | string;
    setupId: string;
    authorizationUrl: string;
    expiresAt: string;
}

export interface McpOAuthSetupStatusResponse {
    connectorId: number | string;
    setupId: string;
    status: string;
    reasonCode?: string | null;
}

export interface ConnectMcpConnectorOptions {
    testConnection?: boolean;
    syncTools?: boolean;
    onConnectorCreated?: (connector: McpConnectorResponse) => void;
}

export class McpConnectorService {
    private api: Api;

    constructor(apiConfig: ApiConfig) {
        this.api = new Api(apiConfig);
    }

    async list(workspaceId: string, signal?: AbortSignal): Promise<McpConnectorResponse[]> {
        return this.api.get<McpConnectorResponse[]>(
            `/api/workspaces/${workspaceId}/mcp-connectors`,
            signal ? { signal } : undefined,
        );
    }

    async create(
        workspaceId: string,
        request: CreateMcpConnectorRequest,
        signal?: AbortSignal,
    ): Promise<McpConnectorResponse> {
        return this.api.post<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors`,
            request,
            signal ? { signal } : undefined,
        );
    }

    async setCredential(
        workspaceId: string,
        connectorId: number | string,
        request: McpCredentialRequest,
    ): Promise<McpConnectorResponse> {
        return this.api.post<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/credential`,
            request,
        );
    }

    async repairLegacyQueryCredential(
        workspaceId: string,
        connectorId: number | string,
    ): Promise<McpConnectorResponse> {
        return this.api.post<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/repair-credential`,
        );
    }

    async startOAuth(
        workspaceId: string,
        connectorId: number | string,
        signal?: AbortSignal,
    ): Promise<McpOAuthStartResponse> {
        return this.api.post<McpOAuthStartResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/oauth/start`,
            undefined,
            signal ? { signal } : undefined,
        );
    }

    async getOAuthSetupStatus(
        workspaceId: string,
        setupId: string,
        signal?: AbortSignal,
    ): Promise<McpOAuthSetupStatusResponse> {
        return this.api.get<McpOAuthSetupStatusResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/oauth/setups/${setupId}`,
            signal ? { signal } : undefined,
        );
    }

    async testConnection(
        workspaceId: string,
        connectorId: number | string,
    ): Promise<McpConnectorResponse> {
        return this.api.post<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/test`
        );
    }

    async syncTools(
        workspaceId: string,
        connectorId: number | string,
    ): Promise<McpConnectorResponse> {
        return this.api.post<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/sync-tools`
        );
    }

    async updateTool(
        workspaceId: string,
        connectorId: number | string,
        toolId: number | string,
        request: McpConnectorToolUpdateRequest,
    ): Promise<McpConnectorResponse> {
        return this.api.patch<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/tools/${toolId}`,
            request,
        );
    }

    async updateResourceContext(
        workspaceId: string,
        connectorId: number | string,
        request: McpResourceContextUpdateRequest,
    ): Promise<McpConnectorResponse> {
        return this.api.patch<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}/resource-context`,
            request,
        );
    }

    async disconnect(
        workspaceId: string,
        connectorId: number | string,
    ): Promise<McpConnectorResponse> {
        return this.api.delete<McpConnectorResponse>(
            `/api/workspaces/${workspaceId}/mcp-connectors/${connectorId}`,
        );
    }

    async connect(
        workspaceId: string,
        input: NormalizedMcpConnectorInput,
        options: ConnectMcpConnectorOptions = {},
    ): Promise<McpConnectorResponse> {
        const shouldTestConnection = options.testConnection ?? true;
        const shouldSyncTools = options.syncTools ?? true;
        let connector = await this.create(workspaceId, input.createRequest);
        try {
            options.onConnectorCreated?.(connector);
        } catch {
            console.warn('[McpConnectorService] Connector-created observer failed');
        }
        const credentialRequest = buildMcpCredentialRequest(input);

        if (credentialRequest) {
            connector = await this.setCredential(workspaceId, connector.connectorId, credentialRequest);
        }

        if (shouldTestConnection) {
            connector = await this.testConnection(workspaceId, connector.connectorId);
        }

        if (shouldSyncTools) {
            connector = await this.syncTools(workspaceId, connector.connectorId);
        }

        return connector;
    }
}

export const mcpConnectorService = new McpConnectorService({
    baseURL: config.services.auth.apiHost,
    headers: { 'Content-Type': 'application/json' },
});

export * from './mcpConnectorInput';
